import { ActionReducerMapBuilder, createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import isString from 'lodash/isString'

import {
    AnalyticsUserAction,
    DRAW_TOOL_ACTION,
    IMUP2DCoordinatesToUpdate,
    IMUP2DDrawableCutout,
    IMUP2DDrawableLocation,
    IMUPState,
    REGION_ENUMS,
} from '../types'
import { BEAM_TYPES } from '../utils/constants'
import { gotProjectAction } from './project'
import { SET_ACTIVE_FLOOR } from '../../actions/drawable'
import { Coordinate } from '../../models/activeDrawable'
import { ActiveFloor } from '../../models/activeFloor'
import { AIAutomatedObject, AISuggestion } from '../../models/aiClassifications'
import { Project } from '../../models/project'
import { PreparedTakeoffData } from '../../models/takeoff'
import { DRAWABLE_TYPES, PAPER_ELEMENT_TYPE } from '../../shared/constants/drawable-types'
import { RootState } from '../../stores'
import { convertScaleFactorLabelEnumToDecimal } from '../../utils/calculations/scaleConversion/scaleConversion'

export type State2D = {
    selectedItem: paper.Item['id'] | null
    selectedPaperIdToDelete: paper.Item['id'] | null
    keyboardMultiSelectedItems: paper.Item['id'][] | null
    imagePaperId: paper.Raster['id'] | null
    openingGroupId: number | null
    documentChunkIds: number[]
    drawableLocations: IMUP2DDrawableLocation[]
    drawablesToCreate: number[]
    drawablesToDelete: number[]
    highlightsToCreate: number[]
    drawablesToCreateByTool: number[]
    errors: string[]
    activeDrawableId: number | null
    activeOpeningLocationId: number | null
    coordinatesToUpdate: IMUP2DCoordinatesToUpdate | null
    zoomButtonIncrement: number
    scaleFactor: number
    regionId: number | null
    defaultZoom: number
    defaultCenter: number[]
    editingActive: boolean
    exiting: boolean
    currentZoom: number
    selectedMaterialType: DRAWABLE_TYPES | REGION_ENUMS | null
    classificationErrorMessage: string | null
    maxDimension: number
    drawToolCreationFlag: DRAW_TOOL_ACTION
    newCreatedDrawables: number[]
    aiSuggestions: AIAutomatedObject[] | null
    selectedAIMaterialType: DRAWABLE_TYPES | REGION_ENUMS | null
    forceOpenAIFolders: boolean
    takeoffData: PreparedTakeoffData | {}
    selectedAiSuggestionIds: string[]
    activeHighlightId: number | null
    // The opening id of the currently-selected DRAWABLE_TYPES.HIGHLIGHTER Opening, for flag
    // linking. Only ever set from addSelectFunctionalityToHighlight (attached exclusively to
    // Highlight tool paths), so this can never be a DRAWABLE_TYPES.NOTE sticky-note opening --
    // an unrelated concept from a flag's own `type` of `note` (see models/flags.ts FlagEnum).
    activeHighlightOpeningId: number | null
    // Active overlay ids from the toolbar Overlay dropdown. Last entry is "focused" -- see
    // selectFocusedOverlayChunkId.
    currentOverlayChunkIds: number[]
    // Which overlay's controls are shown -- set by clicking its raster. Can go stale; read via
    // selectDisplayedOverlayChunkId instead of this raw field.
    selectedOverlayChunkId: number | null
    // Same idea as currentOverlayChunkIds, for the plans-tree's option/base-home estimate flow.
    currentEstimatedOptionOverlayChunkIds: number[]
    overlayChunkOpacityDictionary: Record<number, number>
    overlayColorDictionary: Record<number, string>
    drawableIdsClipboard: number[]
    multiSelectedDrawableIds: number[]
    multiSelectedDrawableType: PAPER_ELEMENT_TYPE | null
    lastOverlayInsertTime: number | null
    plansTreePageVisibilityDictionary: Record<number, boolean>
    classificationOverlaidChunkId: number | null
    selectedMaterialSubType: DRAWABLE_TYPES | BEAM_TYPES | null
    activeAiDrawableGroup: AISuggestion | null
    automatedSpecSheetMarkdown: string | null
    latestAnalyticsAction: AnalyticsUserAction | null
    rimboardPendingBorderIds: number[] | null
    rimboardAutoFloorLevel: string | null
    rimboardParentSettings: { floor_level?: string; location?: string; secondary_location?: string } | null
}

// Fallback used wherever a document chunk doesn't have its own entry yet in
// overlayColorDictionary (e.g. an overlay that's never had its color explicitly set).
export const DEFAULT_OVERLAY_COLOR = '#0086C9'

/**
 * See for explanation of values: https://www.archisoup.com/architectural-scale
 */
export const scaleMap = {
    '1:2': 25,
    '1:4': 50,
    '1:8': 100,
    default: 25,
}

export const initial2DState: State2D = {
    selectedItem: null,
    selectedPaperIdToDelete: null,
    keyboardMultiSelectedItems: null,
    imagePaperId: null,
    documentChunkIds: [],
    drawableLocations: [],
    drawablesToCreate: [],
    drawablesToDelete: [],
    highlightsToCreate: [],
    drawablesToCreateByTool: [],
    openingGroupId: null,
    errors: [],
    activeDrawableId: null,
    zoomButtonIncrement: 0.5,
    activeOpeningLocationId: null,
    coordinatesToUpdate: null,
    scaleFactor: scaleMap.default,
    regionId: null,
    defaultZoom: 1,
    defaultCenter: [0, 0],
    editingActive: false,
    exiting: false,
    currentZoom: 1,
    selectedMaterialType: null,
    classificationErrorMessage: null,
    maxDimension: 0,
    drawToolCreationFlag: DRAW_TOOL_ACTION.NEW_OPENING,
    newCreatedDrawables: [],
    aiSuggestions: null,
    selectedAIMaterialType: null,
    forceOpenAIFolders: false,
    takeoffData: {},
    selectedAiSuggestionIds: [],
    activeHighlightId: null,
    activeHighlightOpeningId: null,
    currentOverlayChunkIds: [],
    selectedOverlayChunkId: null,
    currentEstimatedOptionOverlayChunkIds: [],
    overlayChunkOpacityDictionary: {},
    overlayColorDictionary: {},
    drawableIdsClipboard: [],
    multiSelectedDrawableIds: [],
    multiSelectedDrawableType: null,
    lastOverlayInsertTime: null,
    plansTreePageVisibilityDictionary: {},
    classificationOverlaidChunkId: null,
    selectedMaterialSubType: null,
    activeAiDrawableGroup: null,
    automatedSpecSheetMarkdown: null,
    latestAnalyticsAction: null,
    rimboardPendingBorderIds: null,
    rimboardAutoFloorLevel: null,
    rimboardParentSettings: null,
}

export const getCurrentDrawableLocations = createSelector(
    (state: IMUPState) => {
        return state['2D'].drawableLocations
    },
    (drawableLocations) => drawableLocations
)

export const getAutomatedSpecSheetMarkdown = createSelector(
    (state: RootState) => {
        return state.IMUP['2D'].automatedSpecSheetMarkdown
    },
    (automatedSpecSheetMarkdown) => automatedSpecSheetMarkdown
)

export const getCurrentDrawableLocationsFromRootState = createSelector(
    (state: RootState) => {
        return state.IMUP['2D'].drawableLocations
    },
    (drawableLocations) => drawableLocations
)

export const getOpacities = createSelector(
    (state: RootState) => {
        return { lineOpacity: state.IMUP.tools.lineOpacityValue, areaOpacity: state.IMUP.tools.areaOpacityValue }
    },
    (opacities) => opacities
)

export const drawableLocationSelector = createSelector(
    (state: RootState): IMUP2DDrawableLocation[] => getCurrentDrawableLocations(state.IMUP),
    (state: IMUP2DDrawableLocation[]): IMUP2DDrawableLocation[] => state
)

type PaperSelectionActionPayload = {
    selectedItem: number | null
    activeDrawableId: number | null
    activeOpeningLocationId: number | null
    openingGroupId: number | null
}

const handleSetLatestAnalyticsAction = (state: State2D, { payload }: PayloadAction<AnalyticsUserAction>) => {
    state.latestAnalyticsAction = payload
}

const handleSelectedPaperItemAction = (
    state: State2D,
    { payload }: PayloadAction<PaperSelectionActionPayload>
): void => {
    state.selectedItem = payload.selectedItem
    state.activeDrawableId = payload.activeDrawableId
    state.activeOpeningLocationId = payload.activeOpeningLocationId
    state.openingGroupId = payload.openingGroupId
}

const handleScaleFactor = (state: State2D, { payload }: PayloadAction<ActiveFloor | { scale_factor: number }>) => {
    if (isString(payload.scale_factor)) {
        state.scaleFactor = convertScaleFactorLabelEnumToDecimal(payload.scale_factor ?? '')
    } else {
        state.scaleFactor = payload.scale_factor
    }
}

const handleResetVisibilityOnProjectChange = (state: State2D, _: PayloadAction<Project>) => {
    state.plansTreePageVisibilityDictionary = {}
}

const handleUpdateRegionId = (state: State2D, { payload }: PayloadAction<{ regionId: number | null }>) => {
    state.regionId = payload.regionId
}

const handleUpdateSelectedPaperId = (state: State2D, { payload }: PayloadAction<number | null>) => {
    state.selectedItem = payload
}

function handleEditing(state: State2D, { payload }: PayloadAction<boolean>) {
    state.editingActive = payload
}

function handleSetDefaultZoom(state: State2D, action: PayloadAction<number>): void {
    state.defaultZoom = action.payload
}

function handleSetDefaultCenter(state: State2D, action: PayloadAction<number[]>): void {
    state.defaultCenter = action.payload
}

const handleCurrentZoom = (state: State2D, { payload }: PayloadAction<number>) => {
    state.currentZoom = payload
}

const handleSetMaxDimension = (state: State2D, { payload }: PayloadAction<number>) => {
    state.maxDimension = payload
}

const handleSetFlag = (state: State2D, { payload }: PayloadAction<DRAW_TOOL_ACTION>) => {
    state.drawToolCreationFlag = payload
}

const handleSetClassificationErrorMessage = (state: State2D, { payload }: PayloadAction<string | null>) => {
    state.classificationErrorMessage = payload
}

const handleSetAutomatedSpecSheetMarkdown = (state: State2D, { payload }: PayloadAction<string | null>) => {
    state.automatedSpecSheetMarkdown = payload
}

export function preparedDrawablesSuccessAction(
    state: State2D,
    action: PayloadAction<{ documentChunkIds: number[]; drawableLocations: IMUP2DDrawableLocation[] }>
): void {
    state.documentChunkIds = action.payload.documentChunkIds
    state.drawableLocations = action.payload.drawableLocations
}

function handleSetDrawableLocations(state: State2D, action: PayloadAction<IMUP2DDrawableLocation[]>): void {
    state.drawableLocations = action.payload
}

function insertDrawableLocationAction(state: State2D, action: PayloadAction<IMUP2DDrawableLocation>): void {
    state.drawableLocations = [...state.drawableLocations, action.payload]
}

function updateDrawableLocationCoordinatesAction(
    state: State2D,
    action: PayloadAction<{
        opening_location_id: number
        opening_id?: number
        opening_group_id?: number
        settings?: IMUP2DDrawableLocation['settings']
        coordinates: Coordinate[]
        linear_total?: number | null
        quantity: number | null
        cutouts?: IMUP2DDrawableCutout[]
        region_id: number | null
    }>
): void {
    const locationToUpdateId = action.payload.opening_location_id

    // A move may replace the opening or its group. Do not reactivate a selection
    // that was canceled or changed while the request was pending.
    if (state.activeOpeningLocationId === locationToUpdateId && state.activeDrawableId !== null) {
        state.activeDrawableId = action.payload.opening_id ?? state.activeDrawableId
        state.openingGroupId = action.payload.opening_group_id ?? state.openingGroupId
    }

    state.drawableLocations = state.drawableLocations.map((originalLocation) => {
        if (locationToUpdateId === originalLocation.opening_location_id) {
            const updatedLocation = {
                ...originalLocation,
                opening_id: action.payload.opening_id ?? originalLocation.opening_id,
                drawable_id: action.payload.opening_id ?? originalLocation.drawable_id,
                opening_group_id: action.payload.opening_group_id ?? originalLocation.opening_group_id,
                additionalData: {
                    ...originalLocation.additionalData,
                },
                coordinates: action.payload.coordinates,
                cutouts: action.payload.cutouts ?? originalLocation.cutouts,
                settings: {
                    ...originalLocation.settings,
                    ...action.payload.settings,
                    linear_total: action.payload.linear_total || originalLocation.settings.linear_total,
                    quantity: action.payload.quantity,
                },
                region_id: action.payload.region_id,
            }

            return updatedLocation
        }

        return originalLocation
    })
}

function handleCreateDrawableLocation(state: State2D, action: PayloadAction<number>): void {
    state.drawablesToCreate = [...state.drawablesToCreate, action.payload]
}

function handleCreateMultipleDrawableLocations(state: State2D, action: PayloadAction<number[]>): void {
    state.drawablesToCreate = [...state.drawablesToCreate, ...action.payload]
}

function handleCreateDrawableByTool(state: State2D, action: PayloadAction<number>): void {
    state.drawablesToCreateByTool = [...state.drawablesToCreateByTool, action.payload]
}

function handleResetNewCreateDrawableByTool(state: State2D): void {
    state.drawablesToCreateByTool = []
}

function handleNewCreatedDrawables(state: State2D, action: PayloadAction<number>): void {
    state.newCreatedDrawables = [...state.newCreatedDrawables, action.payload]
}

function handleMultipleNewCreatedDrawables(state: State2D, action: PayloadAction<number[]>): void {
    state.newCreatedDrawables = [...state.newCreatedDrawables, ...action.payload]
}

function handleResetNewCreatedDrawables(state: State2D): void {
    state.newCreatedDrawables = []
}

function handleDeleteDrawableLocation(state: State2D, action: PayloadAction<number[]>): void {
    // reset the state to clear selection since the deletion will remove the selected drawable
    state.activeDrawableId = null
    state.openingGroupId = null
    state.activeOpeningLocationId = null
    state.selectedItem = null
    state.drawableLocations = [...state.drawableLocations].filter((l) => !action.payload.includes(l.drawable_id))
}

function handleDeleteAISuggestions(state: State2D, action: PayloadAction<string[]>): void {
    state.aiSuggestions = (state.aiSuggestions ?? []).map((aiSuggestion) => {
        if (action.payload.includes(aiSuggestion.id)) {
            return {
                ...aiSuggestion,
                isDeleted: true,
            }
        }

        return aiSuggestion
    })
}

function handleUnsetActiveDrawableId(state: State2D): void {
    state.activeDrawableId = null
}

function handleDeleteOpeningLocation(state: State2D, action: PayloadAction<number>): void {
    state.drawableLocations = [...state.drawableLocations].filter((l) => l.opening_location_id !== action.payload)
}

function handleGetDrawablesFailure(state: State2D, action: PayloadAction<string>): void {
    state.documentChunkIds = []
    state.drawableLocations = []
    state.errors = [...state.errors, action.payload]
}

function handleErrorReset(state: State2D): void {
    state.errors = []
}

function handleCoordinatesUpdate(state: State2D, { payload }: PayloadAction<IMUP2DCoordinatesToUpdate>): void {
    state.coordinatesToUpdate = {
        coordinates: payload.coordinates,
        measurements: payload.measurements,
        cutouts: payload.cutouts,
    }
}

function handleUpdatedCoordinatesReset(state: State2D): void {
    state.coordinatesToUpdate = null
    state.editingActive = false
}

function handleUpdateSelectedMaterialType(
    state: State2D,
    { payload }: PayloadAction<State2D['selectedMaterialType']>
): void {
    state.selectedMaterialType = payload
}

function handleCreateHighlightLocation(state: State2D, { payload }: PayloadAction<number>): void {
    state.highlightsToCreate = [...state.highlightsToCreate, payload]
}

function handleClearHighlightsToCreate(state: State2D) {
    state.highlightsToCreate = []
}

function handleUpdateHighlightColor(
    state: State2D,
    { payload }: PayloadAction<{ highlightGroupIds: number[]; color: string }>
): void {
    const { highlightGroupIds, color } = payload

    state.drawableLocations = state.drawableLocations.map((location) => {
        if (
            location.drawing_type === DRAWABLE_TYPES.HIGHLIGHTER &&
            highlightGroupIds.includes(location.opening_group_id)
        ) {
            return {
                ...location,
                settings: {
                    ...location.settings,
                    color: color.replace('#', ''),
                },
            }
        }

        return location
    })
}

const handleAiSuggestionsDrawables = (state: State2D, { payload }: PayloadAction<AIAutomatedObject[]>) => {
    state.aiSuggestions = payload
}

const handleSetSelectedPaperIdToDelete = (
    state: State2D,
    { payload }: PayloadAction<State2D['selectedPaperIdToDelete']>
) => {
    state.selectedPaperIdToDelete = payload
}

const handleSetKeyboardMultiSelectedItems = (
    state: State2D,
    { payload }: PayloadAction<State2D['keyboardMultiSelectedItems']>
) => {
    state.keyboardMultiSelectedItems = payload
}

const handleSetTakeoffData = (state: State2D, { payload }: PayloadAction<PreparedTakeoffData>) => {
    const { projectId, takeoffData } = payload

    state.takeoffData[projectId] = takeoffData
}

function handleSetSelectedAiSuggestionIds(state: State2D, action: PayloadAction<string>): void {
    state.selectedAiSuggestionIds = [...state.selectedAiSuggestionIds, action.payload]
}

function handleDeleteSelectedAiSuggestionId(state: State2D, action: PayloadAction<string>): void {
    state.selectedAiSuggestionIds = [...state.selectedAiSuggestionIds].filter((aiID) => aiID !== action.payload)
}

function handleClearSelectedAiSuggestionIds(state: State2D): void {
    state.selectedAiSuggestionIds = []
}

function handleSetActiveHighlightId(state: State2D, action: PayloadAction<number | null>): void {
    state.activeHighlightId = action.payload
}

function handleSetActiveHighlightOpeningId(state: State2D, action: PayloadAction<number | null>): void {
    state.activeHighlightOpeningId = action.payload
}

function handleSetOverlayChunkID(state: State2D, action: PayloadAction<number | null>): void {
    // `action` (the whole action object) is always truthy -- must check `action.payload` so
    // clearing via setOverlayChunkId(null) actually resets this instead of stamping a new time.
    state.lastOverlayInsertTime = action.payload === null ? null : Date.now()
    state.currentOverlayChunkIds = action.payload === null ? [] : [action.payload]
}

function handleAddOverlayChunkId(state: State2D, action: PayloadAction<number>): void {
    state.lastOverlayInsertTime = action ? Date.now() : null

    if (!state.currentOverlayChunkIds.includes(action.payload)) {
        state.currentOverlayChunkIds.push(action.payload)
    }
}

function handleRemoveOverlayChunkId(state: State2D, action: PayloadAction<number>): void {
    state.currentOverlayChunkIds = state.currentOverlayChunkIds.filter((id) => id !== action.payload)
}

function handleSetSelectedOverlayChunkId(state: State2D, action: PayloadAction<number>): void {
    state.selectedOverlayChunkId = action.payload
}

function handleSetEstimatedOptionOverlayChunkId(
    state: State2D,
    action: PayloadAction<{ opt: number | null; base?: number | null; additionalOpts?: number[] }>
): void {
    // opt goes last so it becomes "focused" (see selectFocusedEstimatedOptionOverlayChunkId);
    // additionalOpts are its already-locked siblings.
    state.currentEstimatedOptionOverlayChunkIds =
        action.payload.opt === null ? [] : [...(action.payload.additionalOpts ?? []), action.payload.opt]
}

function handleAddEstimatedOptionOverlayChunkId(state: State2D, action: PayloadAction<number>): void {
    if (!state.currentEstimatedOptionOverlayChunkIds.includes(action.payload)) {
        state.currentEstimatedOptionOverlayChunkIds.push(action.payload)
    }
}

function handleRemoveEstimatedOptionOverlayChunkId(state: State2D, action: PayloadAction<number>): void {
    state.currentEstimatedOptionOverlayChunkIds = state.currentEstimatedOptionOverlayChunkIds.filter(
        (id) => id !== action.payload
    )
}

// Do not use this, this is so that we can return to CR without calling associated saga of
// above action
function handleUpdateOverlayChunkId(state: State2D, action: PayloadAction<number>): void {
    state.lastOverlayInsertTime = action ? Date.now() : null
    state.currentOverlayChunkIds = [action.payload]
}

// Do not use this, this is so that we can return to CR without calling associated saga of
// above action
function handleResetEstimatedOptionOverlayChunkId(state: State2D, _action: PayloadAction<null>): void {
    state.currentEstimatedOptionOverlayChunkIds = []
}

function handleSetOverlayOpacity(state: State2D, action: PayloadAction<{ id: number; opacity: number }>): void {
    const { id, opacity } = action.payload

    state.overlayChunkOpacityDictionary[id] = opacity
}

function handleSetOverlayColor(state: State2D, action: PayloadAction<{ id: number; color: string }>): void {
    const { id, color } = action.payload

    state.overlayColorDictionary[id] = color
}

function handleCopyDrawables(state: State2D, action: PayloadAction<number[]>) {
    state.drawableIdsClipboard = action.payload
}

function handleMultiSelectingDrawables(state: State2D, action: PayloadAction<State2D['multiSelectedDrawableIds']>) {
    state.multiSelectedDrawableIds = action.payload
}

function handleSetMultiSelectingDrawableType(
    state: State2D,
    action: PayloadAction<State2D['multiSelectedDrawableType']>
) {
    state.multiSelectedDrawableType = action.payload
}

function handleForceOpenAIFolders(state: State2D, action: PayloadAction<boolean>) {
    state.forceOpenAIFolders = action.payload
}

function handleUpdatingPlansTreePageVisibility(
    state: State2D,
    action: PayloadAction<{ id: number; visibility: boolean }>
) {
    const { payload } = action
    const { id, visibility } = payload

    state.plansTreePageVisibilityDictionary[id] = visibility
}

function handleUpdateAISuggestionCoordinates(
    state: State2D,
    action: PayloadAction<{ aiID: string; updatedCoordinates: Coordinate[] }>
): void {
    const { aiID, updatedCoordinates } = action.payload

    state.aiSuggestions = (state.aiSuggestions ?? []).map((aiSuggestion) => {
        if (aiSuggestion.id === aiID) {
            return {
                ...aiSuggestion,
                coordinates: updatedCoordinates,
            }
        }

        return aiSuggestion
    })
}

function handleUpdateAISuggestionsVisibility(
    state: State2D,
    action: PayloadAction<{ aiIDs: string[]; isVisible: boolean }>
): void {
    const { aiIDs, isVisible } = action.payload

    state.aiSuggestions = (state.aiSuggestions ?? []).map((aiSuggestion) => {
        if (aiIDs.includes(aiSuggestion.id)) {
            return {
                ...aiSuggestion,
                isVisible,
            }
        }

        return aiSuggestion
    })
}

function handleUpdateClassificationOverlaidChunkId(state: State2D, action: PayloadAction<number | null>) {
    state.classificationOverlaidChunkId = action.payload
}

function handleSetSelectedMaterialSubType(state: State2D, action: PayloadAction<DRAWABLE_TYPES | BEAM_TYPES | null>) {
    state.selectedMaterialSubType = action.payload
}

function handleUpdateSelectedAIMaterialType(state: State2D, action: PayloadAction<string | null>) {
    state.selectedAIMaterialType = action.payload as DRAWABLE_TYPES | REGION_ENUMS | null
}

function handleSetActiveAiDrawableGroup(state: State2D, action: PayloadAction<AISuggestion | null>) {
    state.activeAiDrawableGroup = action.payload
}

function handleSetPendingRimboardBorders(state: State2D, action: PayloadAction<number[]>) {
    state.rimboardPendingBorderIds = action.payload
}

function handleClearPendingRimboardBorders(state: State2D) {
    state.rimboardPendingBorderIds = null
    state.rimboardAutoFloorLevel = null
    state.rimboardParentSettings = null
}

function handleSetRimboardAutoFloorLevel(state: State2D, action: PayloadAction<string | null>) {
    state.rimboardAutoFloorLevel = action.payload
}

function handleSetRimboardParentSettings(
    state: State2D,
    action: PayloadAction<{ floor_level?: string; location?: string; secondary_location?: string } | null>
) {
    state.rimboardParentSettings = action.payload
}

const reducers = {
    selectedPaperItem: handleSelectedPaperItemAction,
    preparedDrawablesSuccess: preparedDrawablesSuccessAction,
    reset2DErrors: handleErrorReset,
    preparedDrawablesFailure: handleGetDrawablesFailure,
    setDefaultZoom: handleSetDefaultZoom,
    setDefaultCenter: handleSetDefaultCenter,
    updateSelectedCoordinates: handleCoordinatesUpdate,
    insertDrawableLocation: insertDrawableLocationAction,
    resetUpdatedCoordinates: handleUpdatedCoordinatesReset,
    updateDrawableLocationCoordinates: updateDrawableLocationCoordinatesAction,
    createDrawableLocation: handleCreateDrawableLocation,
    createMultipleDrawableLocations: handleCreateMultipleDrawableLocations,
    deleteDrawableLocation: handleDeleteDrawableLocation,
    deleteOpeningLocation: handleDeleteOpeningLocation,
    editingActive: handleEditing,
    setCurrentZoom: handleCurrentZoom,
    updateSelectedMaterialType: handleUpdateSelectedMaterialType,
    updateSelectedPaperId: handleUpdateSelectedPaperId,
    updateScaleFactor: handleScaleFactor,
    updateRegionId: handleUpdateRegionId,
    createHighlight: handleCreateHighlightLocation,
    clearHighlightsToCreate: handleClearHighlightsToCreate,
    updateHighlightColor: handleUpdateHighlightColor,
    setClassificationErrorMessage: handleSetClassificationErrorMessage,
    setMaxDimension: handleSetMaxDimension,
    setFlag: handleSetFlag,
    setNewCreatedDrawables: handleNewCreatedDrawables,
    setMultipleNewCreatedDrawables: handleMultipleNewCreatedDrawables,
    resetNewCreatedDrawables: handleResetNewCreatedDrawables,
    setDrawableLocations: handleSetDrawableLocations,
    setAiSuggestions: handleAiSuggestionsDrawables,
    setSelectedPaperIdToDelete: handleSetSelectedPaperIdToDelete,
    setKeyboardMultiSelectedItems: handleSetKeyboardMultiSelectedItems,
    setTakeoffData: handleSetTakeoffData,
    unsetActiveDrawableId: handleUnsetActiveDrawableId,
    createDrawableByTool: handleCreateDrawableByTool,
    resetNewCreatedDrawableByTool: handleResetNewCreateDrawableByTool,
    setSelectedAiSuggestionIds: handleSetSelectedAiSuggestionIds,
    clearSelectedAiSuggestionIds: handleClearSelectedAiSuggestionIds,
    deleteSelectedAiSuggestionId: handleDeleteSelectedAiSuggestionId,
    setActiveHighlightId: handleSetActiveHighlightId,
    setActiveHighlightOpeningId: handleSetActiveHighlightOpeningId,
    setOverlayChunkId: handleSetOverlayChunkID,
    addOverlayChunkId: handleAddOverlayChunkId,
    removeOverlayChunkId: handleRemoveOverlayChunkId,
    setSelectedOverlayChunkId: handleSetSelectedOverlayChunkId,
    updateOverlayChunkId: handleUpdateOverlayChunkId,
    setEstimatedOptionOverlayChunkId: handleSetEstimatedOptionOverlayChunkId,
    addEstimatedOptionOverlayChunkId: handleAddEstimatedOptionOverlayChunkId,
    removeEstimatedOptionOverlayChunkId: handleRemoveEstimatedOptionOverlayChunkId,
    resetEstimatedOptionOverlayChunkId: handleResetEstimatedOptionOverlayChunkId,
    updateOverlayOpacity: handleSetOverlayOpacity,
    updateOverlayColor: handleSetOverlayColor,
    copyDrawables: handleCopyDrawables,
    multiSelectDrawables: handleMultiSelectingDrawables,
    setMultiSelectingDrawableType: handleSetMultiSelectingDrawableType,
    setForceOpenAIFolders: handleForceOpenAIFolders,
    deleteAISuggestions: handleDeleteAISuggestions,
    updatePlansTreePageVisibility: handleUpdatingPlansTreePageVisibility,
    updateAISuggestionCoordinates: handleUpdateAISuggestionCoordinates,
    updateClassificationOverlaidChunkId: handleUpdateClassificationOverlaidChunkId,
    updateAISuggestionsVisibility: handleUpdateAISuggestionsVisibility,
    setSelectedMaterialSubType: handleSetSelectedMaterialSubType,
    updateSelectedAIMaterialType: handleUpdateSelectedAIMaterialType,
    setActiveAiDrawableGroup: handleSetActiveAiDrawableGroup,
    setAutomatedSpecSheetMarkdown: handleSetAutomatedSpecSheetMarkdown,
    setLatestAnalyticsAction: handleSetLatestAnalyticsAction,
    setPendingRimboardBorders: handleSetPendingRimboardBorders,
    clearPendingRimboardBorders: handleClearPendingRimboardBorders,
    setRimboardAutoFloorLevel: handleSetRimboardAutoFloorLevel,
    setRimboardParentSettings: handleSetRimboardParentSettings,
}

const setActiveFloor = createAction<ActiveFloor>(SET_ACTIVE_FLOOR)

const extraReducers = (builder: ActionReducerMapBuilder<State2D>): void => {
    builder.addCase(setActiveFloor, handleScaleFactor)
    // Reset the plans tree state on project change
    builder.addCase(gotProjectAction, handleResetVisibilityOnProjectChange)
}

const slice2D = createSlice({
    name: '2D',
    initialState: initial2DState,
    reducers,
    extraReducers,
})

export const {
    selectedPaperItem,
    preparedDrawablesSuccess,
    preparedDrawablesFailure,
    reset2DErrors,
    setDefaultZoom,
    setDefaultCenter,
    updateSelectedCoordinates,
    resetUpdatedCoordinates,
    updateDrawableLocationCoordinates,
    createDrawableLocation,
    createMultipleDrawableLocations,
    deleteDrawableLocation,
    deleteOpeningLocation,
    insertDrawableLocation,
    editingActive,
    setCurrentZoom,
    updateSelectedMaterialType,
    updateSelectedPaperId,
    updateScaleFactor,
    updateRegionId,
    createHighlight,
    clearHighlightsToCreate,
    updateHighlightColor,
    setClassificationErrorMessage,
    setMaxDimension,
    setFlag,
    setNewCreatedDrawables,
    setMultipleNewCreatedDrawables,
    resetNewCreatedDrawables,
    setDrawableLocations,
    setAiSuggestions,
    setSelectedPaperIdToDelete,
    setKeyboardMultiSelectedItems,
    setTakeoffData,
    unsetActiveDrawableId,
    createDrawableByTool,
    resetNewCreatedDrawableByTool,
    setSelectedAiSuggestionIds,
    clearSelectedAiSuggestionIds,
    deleteSelectedAiSuggestionId,
    setActiveHighlightId,
    setActiveHighlightOpeningId,
    setOverlayChunkId,
    addOverlayChunkId,
    removeOverlayChunkId,
    setSelectedOverlayChunkId,
    setEstimatedOptionOverlayChunkId,
    addEstimatedOptionOverlayChunkId,
    removeEstimatedOptionOverlayChunkId,
    updateOverlayOpacity,
    updateOverlayColor,
    copyDrawables,
    multiSelectDrawables,
    setMultiSelectingDrawableType,
    setForceOpenAIFolders,
    deleteAISuggestions,
    updatePlansTreePageVisibility,
    resetEstimatedOptionOverlayChunkId,
    updateOverlayChunkId,
    updateAISuggestionCoordinates,
    updateClassificationOverlaidChunkId,
    updateAISuggestionsVisibility,
    setSelectedMaterialSubType,
    updateSelectedAIMaterialType,
    setActiveAiDrawableGroup,
    setAutomatedSpecSheetMarkdown,
    setLatestAnalyticsAction,
    setPendingRimboardBorders,
    clearPendingRimboardBorders,
    setRimboardAutoFloorLevel,
    setRimboardParentSettings,
} = slice2D.actions

export const selectActiveHighlightId = createSelector(
    (state: RootState) => state.IMUP['2D'].activeHighlightId,
    (activeHighlightId) => activeHighlightId
)

export const selectActiveHighlightOpeningId = createSelector(
    (state: RootState) => state.IMUP['2D'].activeHighlightOpeningId,
    (activeHighlightOpeningId) => activeHighlightOpeningId
)

export const getSelectedMaterial = createSelector(
    (state: RootState) => state.IMUP['2D'].selectedMaterialType,
    (matType) => matType
)

export const getSelectedAIMaterial = createSelector(
    (state: RootState) => state.IMUP['2D'].selectedAIMaterialType,
    (selectedAIMaterialType) => selectedAIMaterialType
)

export const getCreationFlag = createSelector(
    (state: RootState) => state.IMUP['2D'].drawToolCreationFlag,
    (drawToolCreationFlag) => drawToolCreationFlag
)

export const selectScaleFactor = createSelector(
    (state: RootState) => state.IMUP['2D'].scaleFactor,
    (scaleFactor) => scaleFactor
)

export const selectRegionId = createSelector(
    (state: RootState) => state.IMUP['2D'].regionId,
    (regionId) => regionId
)

export const selectNewCreatedDrawables = createSelector(
    (state: RootState) => state.IMUP['2D'].newCreatedDrawables,
    (newCreatedDrawables) => newCreatedDrawables
)

export const selectClassificationErrorMessage = createSelector(
    (state: RootState) => state.IMUP['2D'].classificationErrorMessage,
    (classificationErrorMessage) => classificationErrorMessage
)

export const selectDrawableLocationsForPage = createSelector(
    (state: RootState) =>
        state.IMUP['2D'].drawableLocations.filter(
            (l) => l.document_chunk_id === state.IMUP['documents'].activeDocumentChunkId
        ),
    (locations) => locations
)

export const selectAiSuggestions = createSelector(
    (state: RootState) => state.IMUP['2D'].aiSuggestions,
    (aiSuggestions) => aiSuggestions
)

export const selectSelectedItem = createSelector(
    (state: RootState) => state.IMUP['2D'].selectedItem,
    (selectedItem) => selectedItem
)

export const selectSelectedPaperIdToDelete = createSelector(
    (state: RootState) => state.IMUP['2D'].selectedPaperIdToDelete,
    (selectedPaperIdToDelete) => selectedPaperIdToDelete
)

export const selectKeyboardMultiSelectedItems = createSelector(
    (state: RootState) => state.IMUP['2D'].keyboardMultiSelectedItems,
    (keyboardMultiSelectedItems) => keyboardMultiSelectedItems
)

export const selectDrawableLocations = createSelector(
    (state: RootState) => state.IMUP['2D'].drawableLocations,
    (drawableLocations) => drawableLocations
)

/** Return all automated_objects per document_mapping */
export const selectAiSuggestionsForPage = createSelector(
    (state: RootState, activeFloorId: number) =>
        state.IMUP['2D'].aiSuggestions?.filter((ai) => ai.document_mapping_id === activeFloorId && !ai.isDeleted),
    (aiSuggestion) => aiSuggestion
)

export const selectTakeoffData = createSelector(
    (state: RootState) => state.IMUP['2D'].takeoffData,
    (takeoffData) => takeoffData
)

export const selectCoordinatesToUpdate = createSelector(
    (state: RootState) => state.IMUP['2D'].coordinatesToUpdate,
    (coordinatesToUpdate) => coordinatesToUpdate
)

export const selectDrawablesToCreateByTool = createSelector(
    (state: RootState) => state.IMUP['2D'].drawablesToCreateByTool,
    (drawablesToCreateByTool) => drawablesToCreateByTool
)

export const selectedAiSuggestionIds = createSelector(
    (state: RootState) => state.IMUP['2D'].selectedAiSuggestionIds,
    (selectedAiSuggestionIds) => selectedAiSuggestionIds
)

// Defaults to [] so callers (and tests that mock a partial 2D state) never have to guard
// against this being missing the way a freshly-added piece of state can be.
export const selectCurrentOverlayChunkIds = createSelector(
    (state: RootState) => state.IMUP['2D'].currentOverlayChunkIds,
    (chunkIds) => chunkIds ?? []
)

// The most-recently-added overlay id, for single-target controls (opacity/color/etc). `null`
// when none active.
export const selectFocusedOverlayChunkId = createSelector(selectCurrentOverlayChunkIds, (chunkIds) =>
    chunkIds.length ? chunkIds[chunkIds.length - 1] : null
)

// The overlay whose controls should show -- last clicked, falling back to the focused one if
// nothing was clicked or the clicked overlay is no longer active.
export const selectDisplayedOverlayChunkId = createSelector(
    selectCurrentOverlayChunkIds,
    (state: RootState) => state.IMUP['2D'].selectedOverlayChunkId,
    selectFocusedOverlayChunkId,
    (chunkIds, selectedId, focusedId) => (selectedId !== null && chunkIds.includes(selectedId) ? selectedId : focusedId)
)

export const selectOpacityDict = createSelector(
    (state: RootState) => state.IMUP['2D'].overlayChunkOpacityDictionary,
    (opacityDict) => opacityDict
)

export const selectLastOverlayInsertTime = createSelector(
    (state: RootState) => state.IMUP['2D'].lastOverlayInsertTime,
    (timeStamp) => timeStamp
)

export const selectOverlayColorDict = createSelector(
    (state: RootState) => state.IMUP['2D'].overlayColorDictionary,
    (overlayColorDictionary) => overlayColorDictionary
)

export const selectMultiSelectedDrawableIds = createSelector(
    (state: RootState) => state.IMUP['2D'].multiSelectedDrawableIds,
    (multiSelectedDrawableIds) => multiSelectedDrawableIds
)

export const selectMultiSelectedDrawableType = createSelector(
    (state: RootState) => state.IMUP['2D'].multiSelectedDrawableType,
    (multiSelectedDrawableType) => multiSelectedDrawableType
)

// Defaults to [] so callers (and tests that mock a partial 2D state) never have to guard
// against this being missing the way a freshly-added piece of state can be.
export const selectCurrentEstimatedOptionOverlayChunkIds = createSelector(
    (state: RootState) => state.IMUP['2D'].currentEstimatedOptionOverlayChunkIds,
    (chunkIds) => chunkIds ?? []
)

// The most-recently-added active estimated-option overlay id, for controls that only ever act
// on one at a time. `null` when no option overlay is active.
export const selectFocusedEstimatedOptionOverlayChunkId = createSelector(
    selectCurrentEstimatedOptionOverlayChunkIds,
    (chunkIds) => (chunkIds.length ? chunkIds[chunkIds.length - 1] : null)
)

// Same idea as selectDisplayedOverlayChunkId, checked against the estimate flow's own array
// (selectedOverlayChunkId is shared across both flows).
export const selectDisplayedEstimatedOptionOverlayChunkId = createSelector(
    selectCurrentEstimatedOptionOverlayChunkIds,
    (state: RootState) => state.IMUP['2D'].selectedOverlayChunkId,
    selectFocusedEstimatedOptionOverlayChunkId,
    (chunkIds, selectedId, focusedId) => (selectedId !== null && chunkIds.includes(selectedId) ? selectedId : focusedId)
)

export const selectRimboardPendingBorderIds = createSelector(
    (state: RootState) => state.IMUP['2D'].rimboardPendingBorderIds,
    (rimboardPendingBorderIds) => rimboardPendingBorderIds
)

export const selectRimboardAutoFloorLevel = createSelector(
    (state: RootState) => state.IMUP['2D'].rimboardAutoFloorLevel,
    (rimboardAutoFloorLevel) => rimboardAutoFloorLevel
)

export const selectRimboardParentSettings = createSelector(
    (state: RootState) => state.IMUP['2D'].rimboardParentSettings,
    (rimboardParentSettings) => rimboardParentSettings
)

export const selectMultiSelectedGroupNamesAndIds = createSelector(
    [
        (state: RootState) => state.IMUP.geometry.geometries,
        (state: RootState) => state.IMUP['2D'].multiSelectedDrawableIds,
    ],
    (geometries, openingLocationIds) => {
        return geometries.reduce((groupsRecord, geo) => {
            if (openingLocationIds.includes(geo.id)) {
                groupsRecord[geo.geometryGroupId] = geo.settings.name ?? ''
            }

            return groupsRecord
        }, {} as Record<number, string>)
    }
)

export const selectCopiedIds = createSelector(
    (state: RootState) => state.IMUP['2D'].drawableIdsClipboard,
    (ids) => ids
)

export const selectMapDrawableLocationsByOpeningLocationId = createSelector(
    (state: RootState) => state.IMUP['2D'].drawableLocations,
    (drawableLocations) => {
        return new Map(drawableLocations.map((location) => [location.opening_location_id, location]))
    }
)

export const selectForceOpenAIFolders = createSelector(
    (state: RootState) => state.IMUP['2D'].forceOpenAIFolders,
    (forceOpenAIFolders) => forceOpenAIFolders
)

export const selectClassificationOverlaidChunkId = createSelector(
    (state: RootState) => state.IMUP['2D'].classificationOverlaidChunkId,
    (id) => id
)

export const selectHiddenAIIds = createSelector(
    (state: RootState) => {
        const aiIds: string[] = []

        state.IMUP['2D'].aiSuggestions?.forEach(({ id, isVisible }) => {
            if (isVisible === false) {
                aiIds.push(id)
            }
        })

        return aiIds
    },
    (ids) => ids ?? []
)

export const selectSelectedMaterialSubType = createSelector(
    (state: RootState) => state.IMUP['2D'].selectedMaterialSubType,
    (selectedMaterialSubType) => selectedMaterialSubType
)

export const selectIMUPDrawableLocations = createSelector(
    (state: RootState) => state.IMUP['2D'].drawableLocations,
    (drawableLocations) => drawableLocations
)

export const selectActiveAiDrawableGroup = createSelector(
    (state: RootState) => state.IMUP['2D'].activeAiDrawableGroup,
    (activeAiDrawableGroup) => activeAiDrawableGroup
)

export default slice2D
